import {
  skyFlightFrame,
  skyFlightPointToLocal,
  skyFlightPointToWorld,
} from './92-sky-flight-frame.mjs';
import { skyLeviathanGroundAt } from './96-sky-leviathan-contacts.mjs';
import { SKY_WORLD_ASSETS } from './86-sky-world-scale.mjs';
import { SKY_TRAVERSAL_TIMING } from './88-sky-encounter-beat.mjs';

/** Initial first-clear tuning in active seconds. Geometry/health are injected
 * from the accepted assembly, never inferred from a rendered whale silhouette. */
export const SKY_LEVIATHAN_POLICY = Object.freeze({
  arrivalSeconds: 12,
  navigationApproachSeconds: 2.4,
  navigationHoldSeconds: 4.8,
  navigationDepartSeconds: 2.4,
  jawSeconds: 2,
  woundTurnSeconds: 1.2,
  breachSeconds: 1.2,
  perimeterSeconds: 150,
  entrySeconds: 60,
  woundSeconds: 150,
  warningSeconds: 15,
  escapeSeconds: 4,
  crashMinimumSeconds: 4,
  crashPoseSeconds: 2,
  crashDescentSeconds: 8,
  settleSeconds: 0.75,
  settledSpeed: 0.5,
  maximumPilotSpeed: 100,
  maximumProjectiles: 128,
});
const finitePoint = (point) => point && ['x', 'y', 'z'].every((key) => Number.isFinite(point[key]));
const copyPoint = (point) => ({ x: point.x, y: point.y, z: point.z });
const dot = (a, b) => a.x * b.x + a.y * b.y + a.z * b.z;
const difference = (a, b) => ({ x: a.x - b.x, y: a.y - b.y, z: a.z - b.z });
const length = (point) => Math.hypot(point.x, point.y, point.z);
const smooth = (value) => {
  const t = Math.max(0, Math.min(1, value));
  return t * t * (3 - 2 * t);
};
const unit = (point) => finitePoint(point) && Math.abs(dot(point, point) - 1) < 1e-6;
const identifier = (value) => typeof value === 'string' && /^[a-z][a-z0-9-]{0,63}$/.test(value);
const projectileId = (value) => Number.isSafeInteger(value) && value > 0;
const terminal = (phase) => ['complete', 'escaped', 'pilot-down'].includes(phase);
const active = (phase) => ['perimeter', 'entry', 'throat', 'chamber', 'wound'].includes(phase);
const deadline = (phase) => SKY_LEVIATHAN_POLICY[`${phase}Seconds`] ?? Infinity;

function portal(value) {
  if (
    !value ||
    !finitePoint(value.center) ||
    ![value.normal, value.right, value.up].every(unit) ||
    Math.abs(dot(value.normal, value.right)) > 1e-6 ||
    Math.abs(dot(value.normal, value.up)) > 1e-6 ||
    Math.abs(dot(value.right, value.up)) > 1e-6 ||
    !Number.isFinite(value.halfWidth) ||
    value.halfWidth <= 0 ||
    !Number.isFinite(value.halfHeight) ||
    value.halfHeight <= 0
  )
    return null;
  return Object.freeze({
    center: Object.freeze(copyPoint(value.center)),
    normal: Object.freeze(copyPoint(value.normal)),
    right: Object.freeze(copyPoint(value.right)),
    up: Object.freeze(copyPoint(value.up)),
    halfWidth: value.halfWidth,
    halfHeight: value.halfHeight,
  });
}

function geometryContract(value) {
  if (!value || !Array.isArray(value.components) || value.components.length > 32) return null;
  const portals = Object.fromEntries(
    ['mouth', 'chamber', 'breach'].map((key) => [key, portal(value[key])]),
  );
  if (Object.values(portals).some((item) => !item)) return null;
  const components = value.components.map((part) => {
    if (
      !part ||
      !identifier(part.id) ||
      !finitePoint(part.center) ||
      !['navigation', 'chamber', 'rib', 'organ', 'fin'].includes(part.kind) ||
      !Number.isFinite(part.radius) ||
      part.radius <= 0 ||
      !Number.isFinite(part.motionRadius ?? 0) ||
      (part.motionRadius ?? 0) < 0 ||
      (part.motionRadius ?? 0) > 10 ||
      !Number.isFinite(part.hp) ||
      part.hp <= 0 ||
      part.hp > 10000 ||
      (part.kind === 'fin' && !['port', 'starboard'].includes(part.side)) ||
      !Array.isArray(part.requires ?? []) ||
      !(part.requires ?? []).every(identifier)
    )
      return null;
    return Object.freeze({
      id: part.id,
      kind: part.kind,
      side: part.kind === 'fin' ? part.side : null,
      center: Object.freeze(copyPoint(part.center)),
      radius: part.radius,
      motionRadius: part.motionRadius ?? 0,
      hp: part.hp,
      requires: Object.freeze([...(part.requires ?? [])]),
    });
  });
  if (components.some((part) => !part)) return null;
  const ids = new Set(components.map((part) => part.id));
  if (
    ids.size !== components.length ||
    components.some((part) => part.requires.some((id) => !ids.has(id) || id === part.id))
  )
    return null;
  // Dependencies must be acyclic and available no later than the component's
  // own phase; otherwise an apparently valid assembly can never be defeated.
  const ranks = { navigation: 0, chamber: 1, rib: 2, organ: 2, fin: 2 };
  const checked = new Set(),
    checking = new Set();
  const visit = (part) => {
    if (checked.has(part.id)) return true;
    if (checking.has(part.id)) return false;
    checking.add(part.id);
    const valid = part.requires.every((id) => {
      const prior = components.find((item) => item.id === id);
      return ranks[prior.kind] <= ranks[part.kind] && visit(prior);
    });
    checking.delete(part.id);
    if (valid) checked.add(part.id);
    return valid;
  };
  if (components.some((part) => !visit(part))) return null;
  const count = (kind) => components.filter((part) => part.kind === kind).length;
  if (
    count('navigation') < 4 ||
    count('navigation') > 6 ||
    count('chamber') !== 1 ||
    !count('rib') ||
    !count('organ')
  )
    return null;
  if (
    ['port', 'starboard'].some(
      (side) => !components.some((part) => part.kind === 'fin' && part.side === side),
    )
  )
    return null;
  const probes = value.crashProbes;
  if (
    !Array.isArray(probes) ||
    probes.length < 2 ||
    probes.length > 256 ||
    !probes.every(finitePoint) ||
    new Set(probes.map((point) => `${point.x},${point.y},${point.z}`)).size !== probes.length
  )
    return null;
  const radius = value.pilotRadius ?? SKY_WORLD_ASSETS.pilot.height / 2;
  if (
    !Number.isFinite(radius) ||
    radius < SKY_WORLD_ASSETS.pilot.height / 2 ||
    Object.values(portals).some((item) => Math.min(item.halfWidth, item.halfHeight) <= radius)
  )
    return null;
  return Object.freeze({
    ...portals,
    components: Object.freeze(components),
    pilotRadius: radius,
    crashProbes: Object.freeze(probes.map((point) => Object.freeze(copyPoint(point)))),
  });
}

/** Create an unstarted Leviathan arrival. Portal/component/probe positions are
 * hull-local metres; frame and pilot are actual world coordinates. Invalid or
 * mechanically unsatisfiable geometry returns null. The copied immutable
 * contract cannot be changed by later artwork/presentation mutations. */
export function createSkyLeviathan({ id, geometry, frame, pilot } = {}) {
  const accepted = geometryContract(geometry),
    acceptedFrame = skyFlightFrame(frame);
  if (!projectileId(id) || !accepted || !acceptedFrame || !finitePoint(pilot)) return null;
  return {
    id,
    geometry: accepted,
    frame: acceptedFrame,
    pilot: copyPoint(pilot),
    phase: 'arrival',
    phaseAge: 0,
    age: 0,
    activeAge: 0,
    centers: Object.fromEntries(
      accepted.components.map((part) => [part.id, copyPoint(part.center)]),
    ),
    parts: Object.fromEntries(
      accepted.components.map((part) => [part.id, { hp: part.hp, disabled: false }]),
    ),
    woundOpen: false,
    exited: false,
    returnAge: 0,
    settleAge: 0,
    eventId: 0,
    events: [],
    hitLedger: {},
    warning: false,
    outcome: null,
  };
}

function event(state, kind, component = null) {
  state.events.push({
    id: ++state.eventId,
    encounterId: state.id,
    kind,
    component,
    phase: state.phase,
  });
}
function enter(state, phase) {
  if (phase === 'escaping') state.escapeJaw = skyLeviathanSystems(state).jaw;
  state.phase = phase;
  state.phaseAge = 0;
  state.warning = false;
  state.hitLedger = {};
  event(state, 'phase-entered');
}
const allDisabled = (state, predicate) =>
  state.geometry.components.filter(predicate).every((part) => state.parts[part.id].disabled);

/** One real escort presents its control lens at a time. The first approach
 * finishes with arrival; missed holds repeat without granting objective damage.
 * Fraction drives physical travel, while exposure opens only at the held pose. */
export function skyLeviathanNavigationBeat(state, age = state?.age) {
  const nav = state?.geometry.components.filter((part) => part.kind === 'navigation');
  if (!nav?.length || !Number.isFinite(age)) return null;
  const approach = SKY_LEVIATHAN_POLICY.navigationApproachSeconds,
    hold = SKY_LEVIATHAN_POLICY.navigationHoldSeconds,
    depart = SKY_LEVIATHAN_POLICY.navigationDepartSeconds,
    duration = approach + hold + depart;
  const clock = Math.max(0, age - SKY_LEVIATHAN_POLICY.arrivalSeconds + approach);
  const within = clock % duration;
  const ease = (value) => {
    const t = Math.max(0, Math.min(1, value));
    return t * t * t * (10 - 15 * t + 6 * t * t);
  };
  return {
    componentId: nav[Math.floor(clock / duration) % nav.length].id,
    fraction:
      (within < approach
        ? ease(within / approach)
        : within < approach + hold
          ? 1
          : 1 - ease((within - approach - hold) / depart)) *
      (['arrival', 'perimeter'].includes(state.phase) ? 1 : 1 - ease(state.phaseAge / 1.6)),
    exposed: state.phase === 'perimeter' && within >= approach && within < approach + hold,
    within,
    duration,
  };
}

/** Component exposure is objective-driven. This is the same authority for
 * collision admission, shutters/materials and the nonverbal firing cue. Unknown
 * IDs and completed/destroyed components are closed, never fallback targets. */
export function skyLeviathanExposed(state, id) {
  const part = state?.geometry.components.find((item) => item.id === id);
  if (!part || state.parts[id].disabled || !part.requires.every((key) => state.parts[key].disabled))
    return false;
  return part.kind === 'navigation'
    ? state.phase === 'perimeter' &&
        skyLeviathanNavigationBeat(state)?.componentId === id &&
        skyLeviathanNavigationBeat(state).exposed
    : part.kind === 'chamber'
      ? state.phase === 'chamber'
      : state.phase === 'wound' && state.woundOpen;
}

/** Rendering/physical pose inputs derived from canonical clocks and parts.
 * No renderer acknowledgement can open a jaw, breach or gameplay control gate. */
export function skyLeviathanSystems(state) {
  if (!state) return null;
  const nav = state.geometry.components.filter((part) => part.kind === 'navigation');
  const fins = (side) =>
    state.geometry.components.filter((part) => part.kind === 'fin' && part.side === side);
  const lift = (side) =>
    fins(side).filter((part) => !state.parts[part.id].disabled).length / fins(side).length;
  const crashPose =
    state.phase === 'crash'
      ? smooth(state.phaseAge / SKY_LEVIATHAN_POLICY.crashPoseSeconds)
      : state.phase === 'complete'
        ? 1
        : 0;
  const fall =
    state.phase === 'crash'
      ? Math.min(1, Math.max(0, state.phaseAge / SKY_LEVIATHAN_POLICY.crashDescentSeconds))
      : state.phase === 'complete'
        ? 1
        : 0;
  const jaw =
    state.phase === 'escaping'
      ? (state.escapeJaw ?? 0) * (1 - smooth(state.phaseAge / SKY_LEVIATHAN_POLICY.jawSeconds))
      : state.phase === 'entry'
        ? smooth(state.phaseAge / SKY_LEVIATHAN_POLICY.jawSeconds)
        : ['throat', 'chamber', 'breakout', 'wound', 'crash', 'complete'].includes(state.phase)
          ? 1 - crashPose
          : 0;
  return {
    controls: active(state.phase),
    protected: !active(state.phase),
    jaw,
    crashPose,
    // Physical descent uses zero acceleration at both ends; scene haze may
    // consume this exact fraction without inventing a second crash clock.
    crashDescent: fall * fall * fall * (10 - 15 * fall + 6 * fall * fall),
    breach: state.woundOpen
      ? state.phase === 'breakout'
        ? smooth(state.phaseAge / SKY_LEVIATHAN_POLICY.breachSeconds)
        : 1
      : 0,
    woundTurn: state.exited
      ? (() => {
          const t = Math.max(
            0,
            Math.min(
              1,
              (state.returnAge - SKY_TRAVERSAL_TIMING.returnDuration) /
                SKY_LEVIATHAN_POLICY.woundTurnSeconds,
            ),
          );
          return t * t * t * (10 - 15 * t + 6 * t * t);
        })()
      : 0,
    navigationPresentation: skyLeviathanNavigationBeat(state),
    navigation: nav.filter((part) => !state.parts[part.id].disabled).length / nav.length,
    portLift: lift('port'),
    starboardLift: lift('starboard'),
    returnProgress: state.exited
      ? Math.min(1, state.returnAge / SKY_TRAVERSAL_TIMING.returnDuration)
      : 0,
    remaining: active(state.phase) ? Math.max(0, deadline(state.phase) - state.phaseAge) : Infinity,
  };
}

function crosses(port, before, after, clearance, aperture = 1) {
  // Cross only when the trailing extent has cleared the plane, not just the
  // reference point. This keeps the protected breakout until the body is outside.
  const a = dot(difference(before, port.center), port.normal) - clearance,
    b = dot(difference(after, port.center), port.normal) - clearance;
  if (a > 0 || b <= 0 || b - a < 1e-9) return false;
  // Check both ends of the swept portal overlap. Testing only the trailing
  // endpoint would admit a fast diagonal that scraped the leading edge.
  return [Math.max(0, (-a - 2 * clearance) / (b - a)), -a / (b - a)].every((t) => {
    const at = Object.fromEntries(
      ['x', 'y', 'z'].map((axis) => [
        axis,
        before[axis] + (after[axis] - before[axis]) * t - port.center[axis],
      ]),
    );
    return (
      Math.abs(dot(at, port.right)) + clearance <= port.halfWidth * aperture &&
      Math.abs(dot(at, port.up)) + clearance <= port.halfHeight * aperture
    );
  });
}

/** Build rigid hull-local contact proxies in world metres. Callers allocate
 * componentIds once from the run's unique-ID owner. Missing/duplicate IDs return
 * no targets rather than overlapping unrelated enemy identities. These are
 * collision snapshots; only applySkyLeviathanContact may change component HP. */
export function skyLeviathanTargets(state, componentIds) {
  if (!state || !componentIds) return [];
  const parts = state.geometry.components;
  const ids = parts.map((part) => componentIds[part.id]);
  if (!ids.every(projectileId) || new Set(ids).size !== ids.length) return [];
  return parts
    .filter((part) => !state.parts[part.id].disabled)
    .map((part) => ({
      id: componentIds[part.id],
      kind: 'leviathan-part',
      contactShape: 'sphere',
      componentId: part.id,
      leviathanId: state.id,
      ...skyFlightPointToWorld(state.frame, state.centers[part.id]),
      contactFrame: skyFlightFrame(state.frame),
      radius: part.radius,
      motionRadius: part.motionRadius ?? 0,
      hp: state.parts[part.id].hp,
      maxHp: part.hp,
      vulnerable: skyLeviathanExposed(state, part.id),
    }));
}

/** Resolve one already-intersected projectile contact from domain81. Unknown
 * components return null for the caller's generic path. Known but closed,
 * malformed, duplicate or exhausted contacts are handled with zero damage.
 * Damage equals actual HP removed; rewards are NOT awarded here. The bounded
 * component-disabled event lets75 settle its own once-only component rewards;
 *81 owns shot metrics/impact art without a second HP decrement or critical bonus.
 */
export function applySkyLeviathanContact(state, hit) {
  const part = state?.geometry.components.find((item) => item.id === hit?.componentId);
  if (!part) return null;
  const result = { handled: true, damage: 0, quality: 'armor', disabled: false };
  if (
    !projectileId(hit.projectileId) ||
    !finitePoint(hit.point) ||
    !Number.isFinite(hit.damage) ||
    hit.damage <= 0 ||
    !skyLeviathanExposed(state, part.id)
  )
    return result;
  const point = skyFlightPointToLocal(state.frame, hit.point);
  if (length(difference(point, state.centers[part.id])) > part.radius + 1e-6) return result;
  const prior = state.hitLedger[hit.projectileId] ?? [];
  if (
    prior.includes(part.id) ||
    (!state.hitLedger[hit.projectileId] &&
      Object.keys(state.hitLedger).length >= SKY_LEVIATHAN_POLICY.maximumProjectiles)
  )
    return result;
  state.hitLedger[hit.projectileId] = [...prior, part.id];
  const target = state.parts[part.id];
  result.damage = Math.min(target.hp, hit.damage);
  result.quality = 'critical';
  target.hp -= result.damage;
  if (!target.hp) {
    target.disabled = true;
    target.disabledAt = state.age;
    result.disabled = true;
    event(state, 'component-disabled', part.id);
    result.eventId = state.eventId;
  }
  return result;
}

function applyContacts(state, input) {
  const ids = input.activeProjectileIds ?? [],
    contacts = input.contacts ?? [];
  if (
    !Array.isArray(ids) ||
    ids.length > SKY_LEVIATHAN_POLICY.maximumProjectiles ||
    !ids.every(projectileId) ||
    !Array.isArray(contacts) ||
    contacts.length > SKY_LEVIATHAN_POLICY.maximumProjectiles
  )
    return;
  const current = new Set(ids);
  state.hitLedger = Object.fromEntries(
    Object.entries(state.hitLedger).filter(([id]) => current.has(Number(id))),
  );
  for (const hit of contacts)
    if (hit && current.has(hit.projectileId)) applySkyLeviathanContact(state, hit);
}

function stepCrash(state, dt, previousFrame, terrainTravel) {
  const speed = length(difference(state.frame.origin, previousFrame.origin)) / dt;
  const angularSpeed = Math.max(
    ...['right', 'up', 'back'].map(
      (axis) =>
        (2 *
          Math.asin(Math.min(1, length(difference(state.frame[axis], previousFrame[axis])) / 2))) /
        dt,
    ),
  );
  const clearances = state.geometry.crashProbes.map((probe) => {
    const world = skyFlightPointToWorld(state.frame, probe);
    return world.y - skyLeviathanGroundAt(world.x, world.z, terrainTravel);
  });
  const contacts = clearances.filter((height) => Math.abs(height) <= 0.08).length;
  state.settleAge =
    contacts >= 2 &&
    clearances.every((height) => height >= -0.01) &&
    speed <= SKY_LEVIATHAN_POLICY.settledSpeed &&
    angularSpeed <= 0.15
      ? state.settleAge + dt
      : 0;
  if (
    state.phaseAge >= SKY_LEVIATHAN_POLICY.crashMinimumSeconds &&
    state.settleAge >= SKY_LEVIATHAN_POLICY.settleSeconds
  ) {
    enter(state, 'complete');
    state.outcome = 'defeated';
    event(state, 'encounter-defeated');
  }
}

/** Advance one bounded fixed step (0<dt<=0.1 seconds). Frame/pilot are supplied
 * by the domain's physical movement, contacts only by its projectile boundary.
 * activeProjectileIds includes impacting IDs for this step and prunes the hit
 * ledger; no ever-growing history is retained. Hits require a finite world
 * point inside the named canonical component region. Moving articulated socket
 * regions will be supplied by the accepted rig contract before live integration;
 * componentCenters must be canonical domain poses within each authored motion
 * envelope; absent poses retain the current rigid hull-local regions.
 * Invalid movement inputs, pause and terminal states are no-ops; malformed
 * contact batches award nothing. No camera/readiness boolean can skip objectives.
 */
export function stepSkyLeviathan(state, dt, input = {}) {
  if (
    !state ||
    terminal(state.phase) ||
    input.paused ||
    !Number.isFinite(dt) ||
    dt <= 0 ||
    dt > 0.1
  )
    return state;
  const frame = skyFlightFrame(input.frame);
  if (!frame || !finitePoint(input.pilot) || !Number.isFinite(input.terrainTravel ?? 0))
    return state;
  const previousFrame = state.frame,
    before = skyFlightPointToLocal(previousFrame, state.pilot),
    after = skyFlightPointToLocal(frame, input.pilot);
  if (length(difference(after, before)) > SKY_LEVIATHAN_POLICY.maximumPilotSpeed * dt + 1e-6)
    return state;
  if (input.componentCenters) {
    if (
      state.geometry.components.some(
        (part) =>
          !finitePoint(input.componentCenters[part.id]) ||
          length(difference(input.componentCenters[part.id], part.center)) >
            part.motionRadius + 1e-6,
      )
    )
      return state;
    state.centers = Object.fromEntries(
      state.geometry.components.map((part) => [
        part.id,
        copyPoint(input.componentCenters[part.id]),
      ]),
    );
  }
  state.events = [];
  if (input.pilotAlive === false) {
    enter(state, 'pilot-down');
    state.outcome = 'pilot-down';
    return state;
  }
  state.frame = frame;
  state.pilot = copyPoint(input.pilot);
  state.age += dt;
  state.phaseAge += dt;
  if (active(state.phase)) state.activeAge += dt;
  applyContacts(state, { ...input, frame });
  const radius = state.geometry.pilotRadius,
    systems = skyLeviathanSystems(state);
  if (state.phase === 'arrival' && state.phaseAge >= SKY_LEVIATHAN_POLICY.arrivalSeconds)
    enter(state, 'perimeter');
  else if (state.phase === 'perimeter' && allDisabled(state, (part) => part.kind === 'navigation'))
    enter(state, 'entry');
  else if (
    state.phase === 'entry' &&
    systems.jaw === 1 &&
    crosses(state.geometry.mouth, before, after, radius, systems.jaw)
  )
    enter(state, 'throat');
  else if (state.phase === 'throat' && crosses(state.geometry.chamber, before, after, radius))
    enter(state, 'chamber');
  else if (state.phase === 'chamber' && allDisabled(state, (part) => part.kind === 'chamber')) {
    state.woundOpen = true;
    enter(state, 'breakout');
    event(state, 'wound-created');
  } else if (state.phase === 'breakout') {
    if (
      !state.exited &&
      systems.breach === 1 &&
      crosses(state.geometry.breach, before, after, radius)
    ) {
      state.exited = true;
      state.returnAge = 0;
      event(state, 'breach-exited');
    } else if (state.exited) {
      const returnSeconds =
        SKY_TRAVERSAL_TIMING.returnDuration + SKY_LEVIATHAN_POLICY.woundTurnSeconds;
      state.returnAge = Math.min(returnSeconds, state.returnAge + dt);
      if (state.returnAge >= returnSeconds) enter(state, 'wound');
    }
  } else if (
    state.phase === 'wound' &&
    allDisabled(state, (part) => ['rib', 'organ', 'fin'].includes(part.kind))
  )
    enter(state, 'crash');
  else if (state.phase === 'crash') stepCrash(state, dt, previousFrame, input.terrainTravel ?? 0);
  else if (state.phase === 'escaping' && state.phaseAge >= SKY_LEVIATHAN_POLICY.escapeSeconds) {
    enter(state, 'escaped');
    state.outcome = 'escaped';
    event(state, 'encounter-escaped');
  }
  // Resolve a valid objective/portal on the boundary tick before its deadline.
  if (active(state.phase) && state.phaseAge >= deadline(state.phase)) enter(state, 'escaping');
  else if (
    active(state.phase) &&
    !state.warning &&
    deadline(state.phase) - state.phaseAge <= SKY_LEVIATHAN_POLICY.warningSeconds
  ) {
    state.warning = true;
    event(state, 'escape-warning');
  }
  return state;
}
